import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ProductInterestRecord {
  id: string;
  product_id: string;
  producer_id: string;
  name: string | null;
  email: string;
  message: string | null;
  created_at: string;
}

export interface ProductInterestCounts {
  [productId: string]: number;
}

/**
 * Fetch interest counts for a list of product IDs (producer view).
 */
export function useProductInterestCounts(productIds: string[]) {
  const [counts, setCounts] = useState<ProductInterestCounts>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!productIds.length) {
      setCounts({});
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        // Use a single query grouped by product_id
        const { data, error } = await supabase
          .from('product_interest')
          .select('product_id')
          .in('product_id', productIds);

        if (error) throw error;
        if (cancelled) return;

        const result: ProductInterestCounts = {};
        for (const row of data || []) {
          result[row.product_id] = (result[row.product_id] || 0) + 1;
        }
        setCounts(result);
      } catch (err) {
        console.error('[useProductInterestCounts]', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [productIds.join(',')]);

  return { counts, loading };
}

/**
 * Fetch all interest records for a single product (producer detail view).
 */
export function useProductInterestList(productId: string | undefined) {
  const [records, setRecords] = useState<ProductInterestRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async () => {
    if (!productId) {
      setRecords([]);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('product_interest')
        .select('*')
        .eq('product_id', productId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRecords((data as ProductInterestRecord[]) || []);
    } catch (err) {
      console.error('[useProductInterestList]', err);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { records, loading, refetch };
}

/**
 * Submit interest for a product via edge function.
 * The edge function handles:
 * - Inserting into product_interest
 * - Enqueuing confirmation email to the user
 * - Enqueuing notification email to the producer (best-effort)
 */
export async function submitProductInterest(params: {
  productId: string;
  producerId: string;
  email: string;
  name?: string;
  message?: string;
}): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('submit-product-interest', {
      body: {
        productId: params.productId,
        producerId: params.producerId,
        email: params.email,
        name: params.name || null,
        message: params.message || null,
      },
    });

    if (error) throw error;

    // Edge function returns { success, interestId } or { error }
    if (data?.error) {
      throw new Error(data.error);
    }

    return { success: true };
  } catch (err: any) {
    console.error('[submitProductInterest]', err);
    return { success: false, error: err.message || 'Kunde inte skicka intresseanmälan' };
  }
}
